/**
 * Organization Hours Dashboard Types
 *
 * Types for the organization-wide hours dashboard feature.
 * Provides administrators with a comprehensive view of volunteer hour health.
 *
 * @module organization-hours-dashboard-types
 */

import type { HourStatus } from "./hour-summary-types"

// =============================================================================
// ENUMS & CONSTANTS
// =============================================================================

export const STATUS_FILTER_OPTIONS = {
  ALL: "all",
  HAS_REMAINING: "has-remaining",
  FULFILLED: "fulfilled",
  DEFICIT: "deficit",
} as const

// =============================================================================
// FILTER TYPES
// =============================================================================

/**
 * Status filter options for filtering breakdown rows
 */
export type StatusFilter =
  (typeof STATUS_FILTER_OPTIONS)[keyof typeof STATUS_FILTER_OPTIONS]

/**
 * Dashboard filter state
 */
export interface DashboardFilters {
  /** Selected year for calculations */
  year: number
  /** Selected team IDs (empty = all teams) */
  teamIds: string[]
  /** Selected role IDs (empty = all roles) */
  roleIds: string[]
  /** Hour status filter */
  statusFilter: StatusFilter
}

// =============================================================================
// SUMMARY TYPES
// =============================================================================

/**
 * Organization-wide hour aggregation summary
 */
export interface OrganizationHoursSummary {
  /** Sum of positive family obligations; signed values remain in family results */
  totalRequired: number
  /** Completed hours applied to positive family obligations */
  totalCompleted: number
  /** Completed activity hours, independent of requirements and membership dates */
  rawTotalCompleted?: number
  /** Sum of each included family's netRemaining value */
  totalRemaining: number
  /** Fulfilled share of positive family obligations, clamped to 0-100% */
  fulfillmentPercentage: number
  /** Count of active persons included */
  activePersonCount: number
  /** Count of active users included */
  activeUserCount: number
}

/**
 * Summary of excluded inactive records for the info banner
 */
export interface ExclusionSummary {
  /** Count of inactive users excluded */
  inactiveUserCount: number
  /** Count of inactive persons excluded */
  inactivePersonCount: number
  /** Whether there are any exclusions to display */
  hasExclusions: boolean
}

// =============================================================================
// BREAKDOWN ROW TYPES
// =============================================================================

/**
 * Per-team hour aggregation row
 */
export interface TeamHourRow {
  teamId: string
  teamName: string
  /** Count of active persons in this team */
  memberCount: number
  /** Sum of required hours for persons in this team */
  totalRequired: number
  /** Completed hours applied to positive family obligations in this team */
  totalCompleted: number
  /** Completed activity hours for selected users, independent of membership dates */
  rawTotalCompleted?: number
  /** Remaining hours: max(0, required - completed) */
  totalRemaining: number
  /** Hour status for color coding */
  status: HourStatus
}

/**
 * Per-guardian hour aggregation row
 */
export interface GuardianHourRow {
  userId: string
  displayName: string
  email: string
  /** User's role ID */
  roleId: string
  /** User's role display name */
  roleName: string
  /** Role's standard hours (negative = credit) */
  roleStandardHours: number
  /** Count of linked children */
  childCount: number
  /** Family total requirement including role adjustments */
  familyRequired: number
  /** Total hours completed by this user */
  totalCompleted: number
  /** Net remaining hours */
  netRemaining: number
  /** Hour status for color coding */
  status: HourStatus
}

/**
 * Per-person hour aggregation row
 */
export interface PersonHourRow {
  personId: string
  displayName: string
  /** Team IDs this person belongs to */
  teamIds: string[]
  /** Team names for display */
  teamNames: string[]
  /** Individual hour requirement */
  required: number
  /** Hours completed by guardians (proportional share) */
  completed: number
  /** Remaining hours: max(0, required - completed) */
  remaining: number
  /** Hour status for color coding */
  status: HourStatus
  /** Count of guardians linked */
  guardianCount: number
  /** Whether person has at least one guardian */
  hasGuardians: boolean
}

// =============================================================================
// DASHBOARD DATA TYPE
// =============================================================================

/**
 * Complete dashboard data returned from aggregation
 */
export interface OrganizationHoursDashboardData {
  /** Organization-wide summary */
  summary: OrganizationHoursSummary
  /** Excluded inactive records info */
  exclusions: ExclusionSummary
  /** Breakdown by team */
  byTeam: TeamHourRow[]
  /** Breakdown by guardian */
  byGuardian: GuardianHourRow[]
  /** Breakdown by person */
  byPerson: PersonHourRow[]
  /** Applied filters */
  filters: DashboardFilters
  /** Data freshness timestamp */
  fetchedAt: Date
}

// =============================================================================
// EXPORT DATA STRUCTURES
// =============================================================================

/**
 * Team export row for Excel/CSV
 */
export interface TeamExportRow {
  Lag: string
  "Antall medlemmer": number
  "Pålagte timer": number
  "Utførte timer": number
  "Gjenstående timer": number
  Status: string
}

/**
 * Guardian export row for Excel/CSV
 */
export interface GuardianExportRow {
  Navn: string
  "E-post": string
  Rolle: string
  Rolletimer: number
  "Antall barn": number
  "Pålagte timer": number
  "Utførte timer": number
  "Gjenstående timer": number
  Status: string
}

/**
 * Person export row for Excel/CSV
 */
export interface PersonExportRow {
  Navn: string
  Lag: string
  "Pålagte timer": number
  "Utførte timer": number
  "Gjenstående timer": number
  Status: string
  "Antall foresatte": number
}

// =============================================================================
// DEFAULT VALUES
// =============================================================================

/**
 * Default filter configuration
 */
export const DEFAULT_FILTERS: DashboardFilters = {
  year: new Date().getFullYear(),
  teamIds: [],
  roleIds: [],
  statusFilter: "all",
}
